<template>
  <BittsCollapse
    v-model:is-open="isOpen"
    :keep-mounted="true"
    :aria-label="partner.partner_name"
    class="irp-partner-row"
    :class="{ 'irp-partner-row--open': isOpen }"
  >
    <template #title>
      <div class="irp-partner-row__header">
        <div class="irp-partner-row__identity">
          <BittsAvatar
            :org="partnerOrg"
            size="small"
            shape="square"
            class="shrink-0"
          />
          <div
            class="irp-partner-row__identity-text ph-no-capture dd-privacy-mask"
          >
            <span class="irp-partner-row__name-line">
              <span
                class="irp-partner-row__name"
                data-testid="irp-partner-row-name"
              >
                {{ partner.partner_name }}
              </span>
              <MatchIssueFlag
                :match-issues="matchIssues"
                class="shrink-0 text-brand-red"
                data-testid="irp-partner-row-match-issue-flag"
              />
            </span>
            <span v-if="subtitle" class="irp-partner-row__subtitle">
              {{ subtitle }}
            </span>
          </div>
        </div>

        <div
          class="irp-partner-row__column irp-partner-row__column--account-type"
        >
          <span class="irp-partner-row__column-label">Account Type</span>
          <span
            class="irp-partner-row__column-value"
            data-testid="irp-partner-row-account-type"
          >
            {{ accountType || '—' }}
          </span>
        </div>

        <div
          class="irp-partner-row__column irp-partner-row__column--populations"
        >
          <span class="irp-partner-row__column-label">Populations</span>
          <div class="irp-partner-row__populations">
            <template v-if="firstPopulation">
              <BittsTag
                :text="firstPopulation.name"
                color="info"
                size="x-small"
                class="irp-partner-row__population-tag"
              />
              <BittsPopover
                v-if="overflowPopulations.length"
                trigger="hover"
                placement="bottomLeft"
                :mount-to-body="true"
              >
                <span class="irp-partner-row__populations-overflow">
                  +{{ overflowPopulations.length }}
                </span>
                <template #content>
                  <div class="irp-partner-row__populations-list">
                    <BittsTag
                      v-for="population in overflowPopulations"
                      :key="population.id"
                      :text="population.name"
                      color="info"
                      size="x-small"
                    />
                  </div>
                </template>
              </BittsPopover>
            </template>
            <span v-else class="irp-partner-row__column-value">—</span>
          </div>
        </div>

        <div class="irp-partner-row__column irp-partner-row__column--contacts">
          <span class="irp-partner-row__column-label">Contacts</span>
          <span
            class="irp-partner-row__column-value"
            data-testid="irp-partner-row-contacts"
          >
            {{ hasContactsCount ? contactsCount : '—' }}
          </span>
        </div>

        <RouterLink
          v-if="partnerDetailsRoute"
          :to="partnerDetailsRoute"
          class="irp-partner-row__profile-link"
          data-testid="irp-partner-row-profile-link"
          @click.stop
        >
          View partner profile
        </RouterLink>
      </div>
    </template>

    <div class="irp-partner-row__body" @click.stop>
      <slot />
    </div>
  </BittsCollapse>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsCollapse,
  BittsPopover,
  BittsTag,
} from '@crossbeam/bitts';

import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import MatchIssueFlag from '@/components/records/MatchIssueFlag.vue';

import {
  useIndividualRecordStore,
  useMatchIssuesStore,
  usePartnersStore,
  usePopulationsStore,
} from '@/stores';
import type { RecordPartnerAnyType } from '@/types/individual_record';

const {
  partner,
  populationIds,
  subtitle = '',
  accountType = '',
  contactsCount = null,
} = defineProps<{
  partner: RecordPartnerAnyType;
  populationIds?: number[];
  subtitle?: string;
  accountType?: string;
  contactsCount?: number | null;
}>();

const partnersStore = usePartnersStore();
const populationsStore = usePopulationsStore();
const matchIssuesStore = useMatchIssuesStore();
const individualRecordStore = useIndividualRecordStore();

const isOpen = defineModel<boolean>('isOpen', { default: false });

const partnerOrg = computed(() =>
  partnersStore.getPartnerOrgByUuid(partner.partner_organization_uuid),
);

const matchIssues = computed(() => {
  const { sourceId, sourcePrimaryKey } = individualRecordStore;
  const partnerOrgId = partnerOrg.value?.id;
  if (!sourceId || !sourcePrimaryKey || !partnerOrgId) return [];
  return matchIssuesStore
    .getMatchIssuesBySourceIdAndRecordId(sourceId, sourcePrimaryKey)
    .filter(
      (issue) =>
        issue.reporting_organization.id === partnerOrgId ||
        issue.partner_organization.id === partnerOrgId,
    );
});

const partnerDetailsRoute = computed<RouteLocationRaw | null>(() => {
  const partnerOrgId = partnerOrg.value?.id;
  if (!partnerOrgId) return null;
  return { name: 'partner_details', params: { partner_org_id: partnerOrgId } };
});

const resolvedPopulationIds = computed(
  () => populationIds ?? partner.partner_population_ids ?? [],
);

const populations = computed(() =>
  resolvedPopulationIds.value
    .map(populationsStore.getPartnerPopulationById)
    .filter((population) => !!population),
);

const firstPopulation = computed(() => populations.value[0]);
const overflowPopulations = computed(() => populations.value.slice(1));

const hasContactsCount = computed(() => contactsCount !== null);
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";
.irp-partner-row {
  @apply rounded-8 px-16 py-8;
}

.irp-partner-row--open {
  @apply bg-neutral-background-weak;
}

.irp-partner-row__header {
  @apply flex items-center justify-between gap-16 w-full;
}

.irp-partner-row__identity {
  @apply flex items-center gap-8 w-[240px] shrink-0 min-w-0;
}

.irp-partner-row__identity-text {
  @apply flex flex-col justify-center min-w-0 leading-4;
}

.irp-partner-row__name-line {
  @apply flex items-center gap-4 min-w-0;
}

.irp-partner-row__name {
  @apply truncate text-sm font-semibold text-neutral-text-strong;
}

.irp-partner-row__subtitle {
  @apply truncate text-sm text-neutral-text-weak;
}

.irp-partner-row__column {
  @apply flex flex-col gap-2 shrink-0;
}

.irp-partner-row__column--account-type {
  @apply w-[160px];
}

.irp-partner-row__column--populations {
  @apply w-[220px];
}

.irp-partner-row__column--contacts {
  @apply w-[80px];
}

.irp-partner-row__column-label {
  @apply text-xs text-neutral-text-weak;
}

.irp-partner-row__column-value {
  @apply text-sm text-neutral-text-strong;
}

.irp-partner-row__populations {
  @apply flex items-center gap-4;
}

.irp-partner-row__population-tag {
  @apply max-w-[160px];
}

.irp-partner-row__population-tag :deep(.ant-tag) {
  @apply mr-0 truncate block;
}

.irp-partner-row__populations-overflow {
  @apply shrink-0 cursor-default text-sm text-neutral-text-weak;
}

.irp-partner-row__populations-list {
  @apply flex flex-col items-start gap-4 p-8;
}

.irp-partner-row__profile-link {
  @apply shrink-0 text-sm font-semibold text-neutral-text whitespace-nowrap;
}

.irp-partner-row__body {
  @apply mt-16 flex flex-col gap-16;
}

.irp-partner-row :deep(.bitts-collapse__header),
.irp-partner-row :deep(.bitts-collapse__prefix-wrapper) {
  @apply h-auto;
}
</style>
